import React, { useState } from 'react'

import {
  Box,
  CellRenderer,
  CellRenderers,
  CircularProgress,
  Column,
  ConfirmDialog,
  RoundedPlainTextButton,
} from '@percept/mui'

import { Check, Close, Edit } from '@percept/mui/icons'

import { MediaAcademyAdminTable, MediaAcademyAdminTableProps } from './MediaAcademyAdminTable'

import { getPaginatedWebinars, useDeleteMediaAcademyWebinar } from '../hooks'

import { utcToZonedTime } from 'date-fns-tz'

import { formatLocal } from '@percept/utils'

import { InfinitePaginationWrapper } from '@percept/types'

import { MediaAcademyWebinar } from '../typings'


type MediaAcademyWebinarTableRow = MediaAcademyWebinar & {
  actions?: null
}

const columns: Column<MediaAcademyWebinarTableRow>[] = [
  {
    key: 'title',
    label: 'Title',
    align: 'left',
  },
  {
    key: 'start_datetime',
    label: 'Date & Time',
    align: 'left',
  },
  {
    key: 'timezone',
    label: 'Timezone',
    align: 'left',
  },
  {
    key: 'image_url',
    label: 'Image',
    align: 'left',
  },
  {
    key: 'actions',
    label: 'Actions',
    align: 'right',
  },
]

const renderers: CellRenderers<MediaAcademyWebinarTableRow> = {
  /* eslint-disable react/prop-types, react/display-name */
  start_datetime: ({ start_datetime, timezone }) => {
    return <>
      { formatLocal(
        utcToZonedTime(new Date(start_datetime), timezone),
        'dd/MM/yy HH:mm'
      )}
    </>
  },
  image_url: ({ image_url }) => {
    if( !image_url ){
      return <Box color='disabled'>N / A</Box>
    }
    return (
      <img
        src={image_url}
        style={{maxWidth: 100, maxHeight: 50}} />
    )
  },
  /* eslint-enable react/prop-types, react/display-name */
}

export type WebinarAdminTableProps = (
  Omit<
    MediaAcademyAdminTableProps<MediaAcademyWebinarTableRow>,
    | 'name'
    | 'queryFn'
    | 'columns'
    | 'renderers'
  > & {
    setEditWebinar: (resource: MediaAcademyWebinarTableRow) => void
    invalidateQuery: () => void
  }
)


const queryFn = (page: number): Promise<InfinitePaginationWrapper<MediaAcademyWebinarTableRow>> => (
  getPaginatedWebinars({
    order: 'DESC',
    page,
    limit: 10,
  })
)

export const WebinarAdminTable = ({
  setEditWebinar,
  invalidateQuery,
  ...props
}: WebinarAdminTableProps): JSX.Element => {

  const [webinarToDelete, setWebinarToDelete] = useState<MediaAcademyWebinar | null>(null)
  
  const deletionQuery = useDeleteMediaAcademyWebinar({
    onSuccess: () => {
      invalidateQuery()
      setWebinarToDelete(null)
    },
  })

  const actionsRenderer: CellRenderer<MediaAcademyWebinarTableRow> = (webinar): JSX.Element => {
    return (
      <Box display='flex' alignItems='center' justifyContent='flex-end'>
        <Box mr={3}>
          <RoundedPlainTextButton
            startIcon={<Edit />}
            size='small'
            variant='contained'
            onClick={(): void => {
              setEditWebinar(webinar)
            }}>
            Edit
          </RoundedPlainTextButton>
        </Box>
        <RoundedPlainTextButton
          color='secondary'
          startIcon={<Close />}
          size='small'
          variant='contained'
          onClick={(): void => {
            setWebinarToDelete(webinar)
          }}>
          Delete
        </RoundedPlainTextButton>
      </Box>
    )
  }

  return (
    <>
      <MediaAcademyAdminTable
        name='webinars'
        queryFn={queryFn}
        columns={columns}
        renderers={{
          ...renderers,
          actions: actionsRenderer,
        }}
        {...props} />
      
      { webinarToDelete && (
        <ConfirmDialog
          title='Delete Webinar'
          confirmText='Delete'
          confirmDisabled={deletionQuery.isLoading}
          confirmIcon={
            deletionQuery.isLoading ?
              <CircularProgress size='1em' color='inherit' /> :
              <Check />
          }
          content={
            <Box fontSize={14}>
              The webinar &apos;{webinarToDelete.title}&apos; will be deleted.
              <p><strong>This action cannot be undone.</strong></p>
            </Box>
          }
          onConfirm={(): void => {
            if( webinarToDelete ){
              deletionQuery.mutate({ id: webinarToDelete.id })
            }
          }}
          onCancel={(): void => setWebinarToDelete(null)} />
      )}
    </>
  )
}
